import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, UploadSimple, ArrowRight, X } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DOC_KINDS = [
  { key: "id", label: "Photo ID" },
  { key: "paystub", label: "Recent Paystub" },
  { key: "w2", label: "W-2 (last year)" },
  { key: "bank_statement", label: "Bank Statement" },
  { key: "reference_letter", label: "Reference Letter" },
];

export default function ApplicationPublic() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [f, setF] = useState({
    full_name: "", dob: "", current_address: "", phone: "", email: "",
    monthly_income: "", employer: "", employer_phone: "",
    move_in_date: "", pets: "",
    reference_1_name: "", reference_1_phone: "",
    reference_2_name: "", reference_2_phone: "",
    cosigner_name: "", cosigner_phone: "",
    notes: "",
    consent_background_check: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploaded, setUploaded] = useState({});     // {kind: filename}
  const [uploadingKind, setUploadingKind] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/application/${token}`)
      .then(r => {
        setCtx(r.data);
        setF(prev => ({
          ...prev,
          full_name: r.data.prospect_name || "",
          email: r.data.prospect_email || "",
        }));
        (r.data.documents || []).forEach(d => setUploaded(prev => ({ ...prev, [d.kind]: d.filename })));
        if (r.data.already_submitted) setDone(true);
      })
      .catch(() => setCtx({ error: true }))
      .finally(() => setLoading(false));
  }, [token]);

  const uploadDoc = async (kind, file) => {
    setUploadingKind(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${BACKEND}/api/public/application/${token}/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(prev => ({ ...prev, [kind]: file.name }));
    } catch (e) {
      alert(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingKind("");
    }
  };

  const submit = async () => {
    if (!f.full_name.trim()) { alert("Full name is required"); return; }
    if (!f.consent_background_check) { alert("Please consent to a background check to submit"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/public/application/${token}`, {
        ...f, monthly_income: f.monthly_income ? parseInt(f.monthly_income) : null,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      alert(e.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="p-8 text-center text-slate-400 font-black uppercase tracking-widest">Loading…</div>;
  if (!ctx || ctx.error) return (
    <div className="p-8 max-w-md mx-auto text-center">
      <X weight="bold" size={40} className="mx-auto text-red-500"/>
      <div className="mt-2 font-display font-black text-2xl uppercase">Application not found</div>
      <p className="text-sm text-slate-600 mt-1">This link may have expired. Contact your property manager for a new one.</p>
    </div>
  );

  const brand = ctx.brand_color || "#00C4CC";

  if (done) return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-lg mx-auto bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] p-8 text-center">
        <CheckCircle weight="fill" size={56} className="mx-auto" style={{color: brand}}/>
        <h1 className="mt-3 font-display font-black text-3xl uppercase tracking-tight">Application received</h1>
        <p className="text-sm text-slate-600 mt-2">
          Thanks, {f.full_name || ctx.prospect_name}. {ctx.business_name} has your application for <b>{ctx.property_address}</b>
          {ctx.unit && ` · Unit ${ctx.unit}`}. They'll be in touch soon.
        </p>
      </div>
    </div>
  );

  const inputCls = "w-full h-10 px-3 border-2 border-slate-300 text-sm font-medium focus:border-[#0f172a] focus:outline-none";

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4">
      <div className="max-w-2xl mx-auto" data-testid="application-public-page">
        <div className="bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] p-6">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Rental Application</div>
          <h1 className="mt-1 font-display font-black text-2xl sm:text-3xl uppercase tracking-tight">{ctx.business_name}</h1>
          <div className="mt-1 text-sm text-slate-600">
            <b>{ctx.property_address}</b>{ctx.unit && ` · Unit ${ctx.unit}`}
          </div>
          <div className="mt-4 space-y-3">
            <Section title="About You">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Full Name *" v={f.full_name} onChange={v => setF({...f, full_name:v})} span cls={inputCls} testId="app-full-name"/>
                <Field label="Date of Birth" v={f.dob} onChange={v => setF({...f, dob:v})} type="date" cls={inputCls} testId="app-dob"/>
                <Field label="Phone" v={f.phone} onChange={v => setF({...f, phone:v})} cls={inputCls} testId="app-phone"/>
                <Field label="Email" v={f.email} onChange={v => setF({...f, email:v})} type="email" cls={inputCls} testId="app-email"/>
                <Field label="Current Address" v={f.current_address} onChange={v => setF({...f, current_address:v})} span cls={inputCls} testId="app-cur-addr"/>
              </div>
            </Section>
            <Section title="Income & Employment">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Monthly Income ($)" v={f.monthly_income} onChange={v => setF({...f, monthly_income:v})} type="number" cls={inputCls} testId="app-income"/>
                <Field label="Employer" v={f.employer} onChange={v => setF({...f, employer:v})} cls={inputCls} testId="app-employer"/>
                <Field label="Employer Phone" v={f.employer_phone} onChange={v => setF({...f, employer_phone:v})} cls={inputCls} testId="app-employer-phone"/>
                <Field label="Desired Move-In Date" v={f.move_in_date} onChange={v => setF({...f, move_in_date:v})} type="date" cls={inputCls} testId="app-movein"/>
                <Field label="Pets (if any)" v={f.pets} onChange={v => setF({...f, pets:v})} span cls={inputCls} testId="app-pets"/>
              </div>
            </Section>
            <Section title="References">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Reference 1 · Name" v={f.reference_1_name} onChange={v => setF({...f, reference_1_name:v})} cls={inputCls} testId="app-ref1-name"/>
                <Field label="Reference 1 · Phone" v={f.reference_1_phone} onChange={v => setF({...f, reference_1_phone:v})} cls={inputCls} testId="app-ref1-phone"/>
                <Field label="Reference 2 · Name" v={f.reference_2_name} onChange={v => setF({...f, reference_2_name:v})} cls={inputCls} testId="app-ref2-name"/>
                <Field label="Reference 2 · Phone" v={f.reference_2_phone} onChange={v => setF({...f, reference_2_phone:v})} cls={inputCls} testId="app-ref2-phone"/>
                <Field label="Cosigner · Name" v={f.cosigner_name} onChange={v => setF({...f, cosigner_name:v})} cls={inputCls} testId="app-cosigner-name"/>
                <Field label="Cosigner · Phone" v={f.cosigner_phone} onChange={v => setF({...f, cosigner_phone:v})} cls={inputCls} testId="app-cosigner-phone"/>
              </div>
            </Section>
            <Section title="Documents (optional but recommended)">
              <div className="space-y-2">
                {DOC_KINDS.map(dk => (
                  <label key={dk.key} className="flex items-center gap-2 border-2 border-dashed border-slate-300 px-3 py-2 hover:border-[#0f172a] cursor-pointer" data-testid={`app-upload-${dk.key}`}>
                    <UploadSimple weight="bold" size={16} className="text-slate-400"/>
                    <span className="text-xs font-bold flex-1">
                      {dk.label}{uploaded[dk.key] && <span className="text-emerald-600"> ✓ {uploaded[dk.key]}</span>}
                    </span>
                    {uploadingKind === dk.key && <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Uploading…</span>}
                    <input type="file" hidden accept="image/*,application/pdf" onChange={e => e.target.files?.[0] && uploadDoc(dk.key, e.target.files[0])}/>
                  </label>
                ))}
              </div>
            </Section>
            <Section title="Notes">
              <textarea rows={3} value={f.notes} onChange={e => setF({...f, notes: e.target.value})} data-testid="app-notes"
                placeholder="Anything else you'd like the property manager to know?"
                className="w-full px-3 py-2 border-2 border-slate-300 text-sm font-medium resize-none focus:border-[#0f172a] focus:outline-none"/>
            </Section>
            <label className="flex items-start gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={f.consent_background_check} onChange={e => setF({...f, consent_background_check: e.target.checked})}
                data-testid="app-consent" className="h-4 w-4 mt-0.5 accent-[#00C4CC]"/>
              <span>I authorize <b>{ctx.business_name}</b> to run a background and credit check as part of this rental application.</span>
            </label>
            <button onClick={submit} disabled={submitting} data-testid="app-submit"
              style={{background: brand}}
              className="w-full h-12 text-white text-sm font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {submitting ? "Submitting…" : <>Submit Application <ArrowRight weight="bold" size={14}/></>}
            </button>
          </div>
        </div>
        <div className="text-center text-[10px] font-black uppercase tracking-widest text-slate-400 mt-4">Powered by Field Radar</div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, v, onChange, span, type = "text", cls, testId }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
      <input type={type} value={v || ""} onChange={e => onChange(e.target.value)} data-testid={testId} className={cls}/>
    </div>
  );
}
